// <ohero-gallery> — occasion-hero photo carousel.
// Swiping is 100% native CSS scroll-snap; this only: unhides the desktop arrows,
// scrolls the track when they're clicked/dots are tapped, and keeps arrows + dots
// in sync with whatever slide the user has scrolled to. Plain custom element (no
// theme Component deps): the hero isn't inside the cart morph target. Cart contract
// untouched — this never reads or writes line items.
class OheroGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ohero-track]');
    this.slides = Array.from(this.querySelectorAll('[data-ohero-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-ohero-dot]'));
    this.prev = this.querySelector('[data-ohero-prev]');
    this.next = this.querySelector('[data-ohero-next]');
    if (!this.track || this.slides.length < 2) return;

    // JS is present → reveal the desktop arrows (CSS keeps them off on touch).
    if (this.prev) this.prev.hidden = false;
    if (this.next) this.next.hidden = false;

    this.index = 0;

    this.prev?.addEventListener('click', () => this.#go(this.index - 1));
    this.next?.addEventListener('click', () => this.#go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.#go(i)));

    // Follow native swipe/scroll → update active dot + arrow states (rAF-throttled).
    let ticking = false;
    this.track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { this.#syncFromScroll(); ticking = false; });
    }, { passive: true });

    this.#update();
  }

  // Scroll the track so slide `i` snaps into view.
  #go(i) {
    i = Math.max(0, Math.min(i, this.slides.length - 1));
    this.track.scrollTo({ left: this.slides[i].offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    this.index = i;
    this.#update();
  }

  // Derive the nearest slide from current scroll position.
  #syncFromScroll() {
    const mid = this.track.scrollLeft + this.track.clientWidth / 2;
    let nearest = 0;
    let best = Infinity;
    this.slides.forEach((s, i) => {
      const center = s.offsetLeft - this.track.offsetLeft + s.clientWidth / 2;
      const d = Math.abs(center - mid);
      if (d < best) { best = d; nearest = i; }
    });
    if (nearest !== this.index) { this.index = nearest; this.#update(); }
  }

  // Reflect this.index onto dots (active) and arrows (disabled at ends).
  #update() {
    this.dots.forEach((dot, i) => {
      const on = i === this.index;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    if (this.prev) this.prev.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index === this.slides.length - 1;
  }
}

if (!customElements.get('ohero-gallery')) {
  customElements.define('ohero-gallery', OheroGallery);
}
